import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { 
  FileText, 
  Download, 
  Calendar, 
  Radio, 
  Zap, 
  AlertTriangle,
  CheckCircle,
  BarChart3,
  FileDown,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { eventsAPI, reportsAPI, systemsAPI } from "@/lib/api";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function ReportsPage({ user, onLogout }) {
  const [events, setEvents] = useState([]);
  const [systems, setSystems] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloadingEvent, setDownloadingEvent] = useState(false);
  const [downloadingQuick, setDownloadingQuick] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, systemsRes] = await Promise.all([
          eventsAPI.getAll(),
          systemsAPI.getAll(),
        ]);
        setEvents(eventsRes.data);
        setSystems(systemsRes.data);
      } catch (error) {
        toast.error("Error al cargar datos");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDownloadPDF = async () => {
    if (!selectedEventId) {
      toast.error("Selecciona un evento");
      return;
    }

    setDownloadingEvent(true);
    try {
      const response = await reportsAPI.downloadPDF(selectedEventId);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const event = events.find((e) => e.id === selectedEventId);
      link.download = `AVX_RF_Report_${event?.name || "event"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Reporte PDF descargado exitosamente");
    } catch (error) {
      // Error handled silently
      toast.error("Error al descargar PDF");
    } finally {
      setDownloadingEvent(false);
    }
  };

  const handleDownloadQuickPDF = async () => {
    if (systems.length === 0) {
      toast.error("No hay sistemas para exportar");
      return;
    }

    setDownloadingQuick(true);
    try {
      const response = await reportsAPI.downloadQuickPDF();
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `AVX_RF_Quick_Report_${format(new Date(), "yyyy-MM-dd")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Reporte rápido descargado");
    } catch (error) {
      // Error handled silently
      toast.error("Error al descargar reporte rápido");
    } finally {
      setDownloadingQuick(false);
    }
  };

  const selectedEvent = events.find((e) => e.id === selectedEventId);

  // Calculate statistics
  const stableCount = systems.filter(s => s.status === "stable").length;
  const warningCount = systems.filter(s => s.status === "warning").length;
  const interferenceCount = systems.filter(s => s.status === "interference").length;
  const avgRF = systems.length > 0 
    ? Math.round(systems.reduce((acc, s) => acc + (s.rf_level || 0), 0) / systems.length)
    : 0;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="top-bar">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#F4F4F5]">
            Reportes PDF
          </h1>
          <p className="text-sm text-[#71717A]">
            Exporta configuraciones RF en documentos profesionales
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Report Options */}
        <div className="lg:col-span-1 space-y-6">
          {/* Event Report */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-sm bg-[#34C759]/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#34C759]" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-[#F4F4F5]">
                  Reporte de Evento
                </h2>
                <p className="text-xs text-[#71717A]">Completo con espectro</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="form-group">
                <label className="form-label">Seleccionar Evento</label>
                <Select
                  value={selectedEventId}
                  onValueChange={setSelectedEventId}
                >
                  <SelectTrigger
                    className="input-tactical"
                    data-testid="event-select"
                  >
                    <SelectValue placeholder="Selecciona un evento" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-white/10">
                    {events.map((event) => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedEvent && (
                <div className="p-4 bg-[#0A0A0A] border border-white/[0.08] rounded-sm">
                  <h3 className="font-medium text-[#F4F4F5] mb-3">
                    {selectedEvent.name}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-[#71717A]">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(selectedEvent.date), "PPP", {
                        locale: es,
                      })}
                    </div>
                    <div className="flex items-center gap-2 text-[#71717A]">
                      <Radio className="w-4 h-4" />
                      {selectedEvent.location}
                    </div>
                  </div>
                </div>
              )}

              <Button
                onClick={handleDownloadPDF}
                disabled={!selectedEventId || downloadingEvent}
                className="btn-tactical btn-tactical-primary w-full"
                data-testid="download-pdf-button"
              >
                {downloadingEvent ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Descargar Reporte Completo
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Quick Report */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-sm bg-[#3B82F6]/20 flex items-center justify-center">
                <Zap className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <div>
                <h2 className="font-heading font-semibold text-[#F4F4F5]">
                  Reporte Rápido
                </h2>
                <p className="text-xs text-[#71717A]">Sistemas actuales</p>
              </div>
            </div>

            <p className="text-sm text-[#71717A] mb-4">
              Genera un PDF rápido con todos los sistemas RF configurados actualmente,
              sin necesidad de seleccionar un evento.
            </p>

            <Button
              onClick={handleDownloadQuickPDF}
              disabled={systems.length === 0 || downloadingQuick}
              className="btn-tactical w-full border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10"
              variant="outline"
              data-testid="download-quick-pdf-button"
            >
              {downloadingQuick ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <FileDown className="w-4 h-4 mr-2" />
                  Descargar Reporte Rápido
                </>
              )}
            </Button>
          </div>

          {/* Report Info */}
          <div className="p-4 bg-[#111111] border border-white/[0.08] rounded-sm">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-[#34C759] mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-[#F4F4F5] mb-2">
                  Contenido del Reporte
                </h3>
                <ul className="text-sm text-[#71717A] space-y-1">
                  <li>• Visualización de espectro RF</li>
                  <li>• Tabla de sistemas configurados</li>
                  <li>• Coordinación de frecuencias</li>
                  <li>• Alertas e interferencias</li>
                  <li>• Recomendaciones técnicas</li>
                  <li>• Metadata del evento</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Preview & Stats */}
        <div className="lg:col-span-2 space-y-6">
          {/* Statistics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Radio className="w-4 h-4 text-[#34C759]" />
                <span className="text-sm text-[#A1A1AA]">Sistemas</span>
              </div>
              <p className="text-2xl font-bold text-[#F4F4F5]">{systems.length}</p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-[#34C759]" />
                <span className="text-sm text-[#A1A1AA]">Estables</span>
              </div>
              <p className="text-2xl font-bold text-[#4ADE80]">{stableCount}</p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-[#FFCC00]" />
                <span className="text-sm text-[#A1A1AA]">Alertas</span>
              </div>
              <p className="text-2xl font-bold text-[#FDE047]">{warningCount + interferenceCount}</p>
            </div>
            
            <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-[#60A5FA]" />
                <span className="text-sm text-[#A1A1AA]">RF Prom.</span>
              </div>
              <p className="text-2xl font-bold text-[#60A5FA]">{avgRF}%</p>
            </div>
          </div>

          {/* Systems Preview Table */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-[#F4F4F5]">
                Vista Previa - Sistemas Actuales
              </h2>
              <span className="text-xs text-[#71717A] px-2 py-1 bg-[#1A1A1A] rounded">
                {systems.length} sistemas
              </span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-[#34C759] animate-spin" />
              </div>
            ) : systems.length === 0 ? (
              <div className="text-center py-12">
                <Radio className="w-12 h-12 mx-auto mb-4 text-[#71717A] opacity-30" />
                <p className="text-[#71717A]">No hay sistemas registrados</p>
                <p className="text-xs text-[#71717A] mt-2">
                  Agrega sistemas en la sección "Sistemas IEM"
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="systems-preview-table">
                  <thead>
                    <tr className="bg-[#1A1A1A]">
                      <th className="text-[#F4F4F5]">Sistema</th>
                      <th className="text-[#F4F4F5]">Modelo</th>
                      <th className="text-[#F4F4F5]">Frecuencia</th>
                      <th className="text-[#F4F4F5]">Canal</th>
                      <th className="text-[#F4F4F5]">Estado</th>
                      <th className="text-[#F4F4F5]">RF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systems.map((system) => (
                      <tr key={system.id} className="border-b border-white/10 hover:bg-white/5">
                        <td className="font-medium text-[#F4F4F5]">
                          {system.name}
                        </td>
                        <td className="text-[#A1A1AA]">{system.model}</td>
                        <td>
                          <span className="font-mono text-[#34C759] font-medium">
                            {system.frequency.toFixed(3)} MHz
                          </span>
                        </td>
                        <td className="font-mono text-[#E4E4E7]">{system.channel}</td>
                        <td>
                          <span
                            className={`px-2 py-1 text-xs font-bold rounded-sm ${
                              system.status === "stable"
                                ? "bg-[#34C759]/30 text-[#4ADE80]"
                                : system.status === "warning"
                                ? "bg-[#FFCC00]/30 text-[#FDE047]"
                                : "bg-[#FF3B30]/30 text-[#FF6B6B]"
                            }`}
                          >
                            {system.status === "stable"
                              ? "ESTABLE"
                              : system.status === "warning"
                              ? "RIESGO"
                              : "INTERFERENCIA"}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-[#27272A] rounded-sm overflow-hidden">
                              <div
                                className="h-full bg-[#34C759]"
                                style={{ width: `${system.rf_level}%` }}
                              />
                            </div>
                            <span className="font-mono text-xs text-[#A1A1AA] font-medium">
                              {system.rf_level}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Events List */}
          <div className="bg-[#111111] border border-white/[0.08] rounded-sm p-6">
            <h2 className="font-heading font-semibold text-[#F4F4F5] mb-4">
              Eventos Disponibles
            </h2>
            
            {events.length === 0 ? (
              <p className="text-[#71717A] text-sm">
                No hay eventos. Crea uno en la sección "Eventos".
              </p>
            ) : (
              <div className="space-y-3">
                {events.slice(0, 5).map((event) => (
                  <div 
                    key={event.id}
                    className={`p-3 rounded-sm border cursor-pointer transition-all ${
                      selectedEventId === event.id 
                        ? "border-[#34C759] bg-[#34C759]/10" 
                        : "border-white/[0.08] hover:border-white/20"
                    }`}
                    onClick={() => setSelectedEventId(event.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#F4F4F5]">{event.name}</p>
                        <p className="text-xs text-[#71717A]">
                          {event.location} • {format(new Date(event.date), "dd MMM yyyy", { locale: es })}
                        </p>
                      </div>
                      {selectedEventId === event.id && (
                        <CheckCircle className="w-5 h-5 text-[#34C759]" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
